/**
 * ============================================================================
 * MOCK DATA — synthetic WebSocket event stream.
 * ============================================================================
 *
 * See `src/mock/banner.ts`. This hook has the same shape as the real
 * `useDashboardSocket` hook (status, attempts, lastEventAt, missedEvents,
 * resyncToken, reconnect) so pages cannot tell the difference in code — but the
 * frames it emits are generated in the browser on a timer. No daemon is
 * involved, and no event describes a real order, position or broker deal.
 *
 * It exists so that live-update plumbing (delta application, sequence tracking,
 * resync-on-gap) is exercised while the Rust API is still being written.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DashboardEvent } from '../api/types';
import type { DashboardSocketState } from '../api/useDashboardSocket';
import { MOCK_DATASET } from './dataset';
import { buildQuotes, buildRibbon } from './fixtures';

export interface UseMockSocketOptions {
  enabled?: boolean;
  onEvent: (event: DashboardEvent) => void;
}

/**
 * Distributive `Omit`, so each union member keeps its own `payload` type instead
 * of collapsing into a union of every payload in the protocol.
 */
type MockEventInput = DashboardEvent extends infer TEvent
  ? TEvent extends DashboardEvent
    ? Omit<TEvent, 'v' | 'seq' | 'emitted_at'>
    : never
  : never;

const HEARTBEAT_MS = 3_000;
const QUOTE_MS = 2_000;
const POSITION_MS = 6_000;
const EVENT_MS = 11_000;

export function useMockSocket(options: UseMockSocketOptions): DashboardSocketState & { reconnect: () => void } {
  const { enabled = true, onEvent } = options;
  const [state, setState] = useState<DashboardSocketState>({
    status: 'idle',
    attempts: 0,
    lastEventAt: null,
    lastSeq: null,
    missedEvents: 0,
    resyncToken: 0,
    unknownTypes: [],
    lastError: null,
  });
  const [nonce, setNonce] = useState(0);

  const handlerRef = useRef(onEvent);
  const seqRef = useRef(0);

  useEffect(() => {
    handlerRef.current = onEvent;
  }, [onEvent]);

  const emit = useCallback((event: MockEventInput): void => {
    seqRef.current += 1;
    const frame = {
      v: 1,
      seq: seqRef.current,
      emitted_at: new Date().toISOString(),
      ...event,
    } as DashboardEvent;
    setState((prev) => ({ ...prev, lastEventAt: frame.emitted_at, lastSeq: seqRef.current }));
    handlerRef.current(frame);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setState((prev) => ({ ...prev, status: 'idle' }));
      return;
    }

    setState((prev) => ({ ...prev, status: 'connecting', lastError: null }));

    // A short delay mirrors a real handshake, so the connecting state is real.
    const openTimer = window.setTimeout(() => {
      setState((prev) => ({ ...prev, status: 'open', attempts: 0 }));

      emit({ type: 'heartbeat', payload: { server_time: new Date().toISOString(), data_freshness: buildRibbon().data_freshness } });
      emit({ type: 'ribbon.updated', payload: buildRibbon() });
    }, 240);

    const heartbeat = window.setInterval(() => {
      emit({
        type: 'heartbeat',
        payload: { server_time: new Date().toISOString(), data_freshness: buildRibbon().data_freshness },
      });
    }, HEARTBEAT_MS);

    let quoteIndex = 0;
    const quotes = buildQuotes();
    const quoteTimer = window.setInterval(() => {
      const quote = quotes[quoteIndex % quotes.length];
      quoteIndex += 1;
      if (quote === undefined) return;
      // Jitter the mock quote so the visible refresh is detectable.
      const drift = (Math.random() - 0.5) * 0.0002;
      const bid = Number((quote.bid + drift).toFixed(quote.bid > 100 ? 3 : 5));
      emit({
        type: 'quote.updated',
        payload: { ...quote, bid, ask: Number((quote.ask + drift).toFixed(quote.bid > 100 ? 3 : 5)), quote_age_ms: 320, last_tick_at: new Date().toISOString(), stale: false },
      });
    }, QUOTE_MS);

    let positionIndex = 0;
    const positionTimer = window.setInterval(() => {
      const position = MOCK_DATASET.positions[positionIndex % Math.max(1, MOCK_DATASET.positions.length)];
      positionIndex += 1;
      if (position === undefined) return;
      emit({
        type: 'position.updated',
        payload: { ...position, unrealized_pnl: Number((position.unrealized_pnl + (Math.random() - 0.5) * 12).toFixed(2)), age_ms: position.age_ms + POSITION_MS },
      });
    }, POSITION_MS);

    let eventIndex = 1;
    const eventTimer = window.setInterval(() => {
      const template = MOCK_DATASET.system_events[eventIndex % MOCK_DATASET.system_events.length];
      eventIndex += 1;
      if (template === undefined) return;
      emit({
        type: 'system.event',
        payload: { ...template, event_id: `${template.event_id}-stream-${String(eventIndex)}`, occurred_at: new Date().toISOString() },
      });
    }, EVENT_MS);

    return () => {
      window.clearTimeout(openTimer);
      window.clearInterval(heartbeat);
      window.clearInterval(quoteTimer);
      window.clearInterval(positionTimer);
      window.clearInterval(eventTimer);
      setState((prev) => ({ ...prev, status: 'closed' }));
    };
  }, [enabled, emit, nonce]);

  const reconnect = useCallback(() => {
    seqRef.current = 0;
    setState((prev) => ({ ...prev, attempts: 0, missedEvents: 0, lastError: null }));
    setNonce((n) => n + 1);
  }, []);

  return useMemo(() => ({ ...state, reconnect }), [state, reconnect]);
}
